import { useCallback, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { atom } from 'jotai';
import { Timestamp } from 'firebase/firestore';

import { Task, TaskStatus, Category } from '@/types';
import { firebaseUserAtom } from '@/stores/auth';
import { DEFAULT_CATEGORIES } from '@/constants/categories';

// Repository
import * as tasksRepository from '@/repositories/tasks.repository';
import { CreateTaskInput } from '@/repositories/tasks.repository';

// Services
import {
  filterTasksByStatus,
  groupTasksByCategory,
  getTaskMetaInfo as serviceGetTaskMetaInfo,
  formatDuration,
} from '@/services';

// ============================================
// Atoms for Task Management
// ============================================

export const taskStatusFilterAtom = atom<TaskStatus | 'all'>('pending');
export const taskViewModeAtom = atom<'list' | 'category'>('list');
export const tasksLoadingAtom = atom<boolean>(false);
export const tasksAtom = atom<Task[]>([]);
export const categoriesAtom = atom<Category[]>([]);

// ============================================
// Types
// ============================================

export interface UseTasksOptions {
  statusFilter?: TaskStatus | 'all';
}

export interface UpdateTaskInput {
  id: string;
  updates: Partial<Omit<Task, 'id' | 'userId' | 'createdAt'>>;
}

// Re-export for consumers
export type { CreateTaskInput };

// ============================================
// useTasks Hook
// ============================================

export function useTasks(options: UseTasksOptions = {}) {
  const firebaseUser = useAtomValue(firebaseUserAtom);
  const [tasks, setTasks] = useAtom(tasksAtom);
  const [categories, setCategories] = useAtom(categoriesAtom);
  const [isLoading, setIsLoading] = useAtom(tasksLoadingAtom);
  const [statusFilter, setStatusFilter] = useAtom(taskStatusFilterAtom);
  const [viewMode, setViewMode] = useAtom(taskViewModeAtom);

  // Fetch tasks from Firestore
  const fetchTasks = useCallback(async () => {
    if (!firebaseUser?.uid) {
      setTasks([]);
      return [];
    }

    setIsLoading(true);
    try {
      const fetchedTasks = await tasksRepository.getTasks(firebaseUser.uid);
      setTasks(fetchedTasks);
      return fetchedTasks;
    } catch (error) {
      console.error('Error fetching tasks:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [firebaseUser?.uid, setTasks, setIsLoading]);

  // Fetch categories (uses defaults for now)
  const fetchCategories = useCallback(async () => {
    const defaultCats = DEFAULT_CATEGORIES.map((cat, index) => ({
      ...cat,
      id: `default-${index}`,
    })) as Category[];
    setCategories(defaultCats);
    return defaultCats;
  }, [setCategories]);

  // Create a new task
  const createTask = useCallback(
    async (input: CreateTaskInput): Promise<Task | null> => {
      if (!firebaseUser?.uid) {
        console.error('User not authenticated');
        return null;
      }

      try {
        const maxOrder = tasks.length > 0 ? Math.max(...tasks.map((t) => t.order)) : 0;
        const categoryId = input.categoryId || categories[0]?.id || 'default-0';
        const newTask = await tasksRepository.createTask(
          firebaseUser.uid,
          { ...input, categoryId },
          maxOrder
        );
        setTasks((prev) => [...prev, newTask]);
        return newTask;
      } catch (error) {
        console.error('Error creating task:', error);
        return null;
      }
    },
    [firebaseUser?.uid, tasks, categories, setTasks]
  );

  // Update an existing task
  const updateTask = useCallback(
    async ({ id, updates }: UpdateTaskInput): Promise<boolean> => {
      if (!firebaseUser?.uid) {
        console.error('User not authenticated');
        return false;
      }

      try {
        await tasksRepository.updateTask(id, updates);
        setTasks((prev) =>
          prev.map((task) =>
            task.id === id ? { ...task, ...updates, updatedAt: Timestamp.now() } : task
          )
        );
        return true;
      } catch (error) {
        console.error('Error updating task:', error);
        return false;
      }
    },
    [firebaseUser?.uid, setTasks]
  );

  // Delete a task
  const deleteTask = useCallback(
    async (taskId: string): Promise<boolean> => {
      if (!firebaseUser?.uid) {
        console.error('User not authenticated');
        return false;
      }

      try {
        await tasksRepository.deleteTask(taskId);
        setTasks((prev) => prev.filter((task) => task.id !== taskId));
        return true;
      } catch (error) {
        console.error('Error deleting task:', error);
        return false;
      }
    },
    [firebaseUser?.uid, setTasks]
  );

  // Reorder tasks
  const reorderTasks = useCallback(
    async (reorderedTasks: Task[]): Promise<boolean> => {
      if (!firebaseUser?.uid) {
        console.error('User not authenticated');
        return false;
      }

      try {
        const taskOrders = reorderedTasks.map((task, index) => ({
          id: task.id,
          order: index,
        }));
        await tasksRepository.reorderTasks(taskOrders);

        setTasks((prev) => {
          const taskMap = new Map(prev.map((t) => [t.id, t]));
          return reorderedTasks.map((task, index) => ({
            ...taskMap.get(task.id)!,
            order: index,
          }));
        });
        return true;
      } catch (error) {
        console.error('Error reordering tasks:', error);
        return false;
      }
    },
    [firebaseUser?.uid, setTasks]
  );

  // Convenience operations
  const completeTask = useCallback(
    (taskId: string) => updateTask({ id: taskId, updates: { status: 'completed' } }),
    [updateTask]
  );

  const scheduleTask = useCallback(
    (taskId: string, blockId: string) =>
      updateTask({ id: taskId, updates: { status: 'scheduled', scheduledBlockId: blockId } }),
    [updateTask]
  );

  const unscheduleTask = useCallback(
    (taskId: string) =>
      updateTask({ id: taskId, updates: { status: 'pending', scheduledBlockId: null } }),
    [updateTask]
  );

  // Derived data using service functions
  const filteredTasks = useMemo(() => {
    const filter = options.statusFilter ?? statusFilter;
    return filterTasksByStatus(tasks, filter);
  }, [tasks, statusFilter, options.statusFilter]);

  const tasksByCategory = useMemo(() => groupTasksByCategory(filteredTasks), [filteredTasks]);

  // Category helpers
  const getCategoryById = useCallback(
    (categoryId: string): Category | undefined => categories.find((cat) => cat.id === categoryId),
    [categories]
  );

  const getTaskCategoryColor = useCallback(
    (task: Task): string => getCategoryById(task.categoryId)?.color || '#999999',
    [getCategoryById]
  );

  const getTaskMetaInfo = useCallback(
    (task: Task): string => serviceGetTaskMetaInfo(task, formatDuration),
    []
  );

  return {
    // Data
    tasks: filteredTasks,
    allTasks: tasks,
    tasksByCategory,
    categories,
    isLoading,

    // Filter state
    statusFilter,
    setStatusFilter,
    viewMode,
    setViewMode,

    // Actions
    fetchTasks,
    fetchCategories,
    createTask,
    updateTask,
    deleteTask,
    reorderTasks,
    completeTask,
    scheduleTask,
    unscheduleTask,

    // Helpers
    getCategoryById,
    getTaskCategoryColor,
    getTaskMetaInfo,
    formatDuration,
  };
}
